import React, { useEffect, useState } from "react";
import { Plus, Upload, Filter, ChevronDown, Clock, AlertCircle, CheckCircle2, Pause } from "lucide-react";
import axios from "axios";
import TaskForm from "../components/tasks/TaskForm";
import TaskCard from "../components/tasks/TaskCard";
import TaskBulkImportModal from "../components/tasks/TaskBulkImportModal";
import "../css/tasks/taskpage.css";

export default function TaskPage() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [sortBy, setSortBy] = useState("dueDate");
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        console.log("User role from token:", payload.role, "isAdmin:", payload.isAdmin);
        setUserRole(payload.role);
      } catch (e) {
        console.error("Error decoding token:", e);
      }
    }
    fetchTasks();
    fetchStats();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://apicrm.zenrealestate.in/api/tasks", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setTasks(response.data.data || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("https://apicrm.zenrealestate.in/api/tasks/stats", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setStats(response.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleTaskCreated = () => {
    setShowForm(false);
    fetchTasks();
    fetchStats();
  };

  const getFilteredTasks = () => {
    let filtered = tasks;
    if (filterStatus === "overdue") {
      const now = new Date();
      filtered = filtered.filter(
        task =>
          task.dueDate &&
          new Date(task.dueDate) < now &&
          task.status !== "completed"
      );
    } else if (filterStatus !== "all") {
      filtered = filtered.filter(task => task.status === filterStatus);
    }
    if (filterPriority !== "all") {
      filtered = filtered.filter(task => task.priority === filterPriority);
    }
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "dueDate":
          return new Date(a.dueDate) - new Date(b.dueDate);
        case "priority":
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case "status":
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  if (loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading tasks</p>
      </div>
    );
  }

  return (
    <div className="tp-page">
      <div className="tp-header">
        <div className="tp-header-content">
          <div className="tp-title-section">
            <h1 className="tp-title">Task Management</h1>
            <p className="tp-subtitle">Track and manage your team tasks</p>
          </div>
          {(userRole === "manager" || userRole === "admin" || userRole === "superadmin") && (
            <div style={{ display: "flex", gap: 10 }}>
              <button className="tp-btn-import" onClick={() => setShowImport(true)}>
                <Upload size={16} />
                Import Excel
              </button>
              <button className="tp-btn-create" onClick={() => setShowForm(true)}>
                <Plus size={18} />
                New Task
              </button>
            </div>
          )}
        </div>
      </div>

      {stats && (
        <div className="tp-stats">
          <div className="tp-stat-card">
            <div className="tp-stat-icon pending">
              <Clock size={20} />
            </div>
            <div className="tp-stat-content">
              <p className="tp-stat-label">Pending</p>
              <p className="tp-stat-value">{stats.pending}</p>
            </div>
          </div>
          <div className="tp-stat-card">
            <div className="tp-stat-icon in-progress">
              <AlertCircle size={20} />
            </div>
            <div className="tp-stat-content">
              <p className="tp-stat-label">In Progress</p>
              <p className="tp-stat-value">{stats.in_progress}</p>
            </div>
          </div>
          <div className="tp-stat-card">
            <div className="tp-stat-icon completed">
              <CheckCircle2 size={20} />
            </div>
            <div className="tp-stat-content">
              <p className="tp-stat-label">Completed</p>
              <p className="tp-stat-value">{stats.completed}</p>
            </div>
          </div>
        </div>
      )}

      <div className="tp-controls">
        <div className="tp-filters">
          <div className="tp-filter-group">
            <Filter size={16} />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="tp-filter-select">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="inProgress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="tp-filter-group">
            <Filter size={16} />
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="tp-filter-select">
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="tp-filter-group">
            <ChevronDown size={16} />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="tp-filter-select">
              <option value="dueDate">Sort by Target Date</option>
              <option value="priority">Sort by Priority</option>
              <option value="status">Sort by Status</option>
            </select>
          </div>
        </div>

        <div className="tp-result-count">
          {filteredTasks.length} task{filteredTasks.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="tp-tasks-container">
        {filteredTasks.length > 0 ? (
          <div className="tp-tasks-grid">
            {filteredTasks.map((task) => (
              <TaskCard
                key={task._id}
                task={task}
                onTaskUpdated={handleTaskCreated}
              />
            ))}
          </div>
        ) : (
          <div className="tp-empty-state">
            <div className="tp-empty-icon">
              <CheckCircle2 size={48} />
            </div>
            <p className="tp-empty-title">No tasks found</p>
          </div>
        )}
      </div>

      {showForm && (
        <TaskForm
          onClose={() => setShowForm(false)}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {showImport && (
        <TaskBulkImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            fetchTasks();
            fetchStats();
          }}
        />
      )}
    </div>
  );
}
